/**
 * CalendarMeetingsTab — SUN-70. AURA's own Calendar and Meetings screens, reused as they are,
 * plus "Online meeting" (Zoom / Google Meet, SUN-62).
 *
 * Meetings shows only for the roles the /meetings route lets in (routes/salesRoutes.tsx);
 * everyone else gets Calendar + Online meeting. The server still scopes every list.
 */
import { lazy, Suspense, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { OnlineMeetingPanel } from './OnlineMeetingPanel';

const CalendarPage = lazy(() => import('../../pages/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })));
const MeetingsPage = lazy(() => import('../../pages/meetings/MeetingsPage').then((m) => ({ default: m.MeetingsPage })));

/** Same list as the /meetings route's RequireRoles. */
export const MEETINGS_ROLES = [
  'owner', 'admin', 'super_admin', 'manager', 'sales_manager',
  'sr_sales_manager', 'manager_sales', 'operations_manager',
];

type Sub = 'calendar' | 'meetings' | 'online';

export function CalendarMeetingsTab() {
  const roleName = useAuthStore((s) => s.user?.role?.name ?? '');
  const canMeetings = MEETINGS_ROLES.includes(roleName);
  const [sub, setSub] = useState<Sub>('calendar');
  const subs: { key: Sub; label: string }[] = [
    { key: 'calendar', label: 'Calendar' },
    ...(canMeetings ? [{ key: 'meetings' as const, label: 'Meetings' }] : []),
    { key: 'online', label: 'Online meeting' },
  ];
  const active = sub === 'meetings' && !canMeetings ? 'calendar' : sub;

  return (
    <>
      <div className="kw-tabs kw-sub" role="tablist" aria-label="Calendar and meetings">
        {subs.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={active === s.key}
            className={`kw-tab${active === s.key ? ' kw-sub-on' : ''}`}
            onClick={() => setSub(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <Suspense fallback={<div className="kw-pad kw-small">Loading…</div>}>
        {active === 'calendar' && <div className="kw-scroll kw-page-host" data-testid="cal-host"><CalendarPage /></div>}
        {active === 'meetings' && <div className="kw-scroll kw-page-host" data-testid="meetings-host"><MeetingsPage /></div>}
      </Suspense>
      {active === 'online' && <OnlineMeetingPanel />}
    </>
  );
}
